import { Box, Card, Link, Section, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import {
  AtsJoinPromotionSettingsDocument,
  CreateJoinCompanyDocument,
} from 'generated/resources/graphql'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ApprovalsBlock from 'modules/approvals/components/ApprovalsConfigurationBlock'
import { useApprovalFeatures } from 'modules/approvals/hooks/useFeatureLimits'
import { useAtsContext } from 'modules/ats/lib/atsContext'

import { ExternalProfiles } from './ExternalProfiles'

export const JoinPromotionSettings = () => {
  const { company } = useCurrentSession()
  const { joinEnabled, refetch } = useAtsContext()
  const { canAddGroup } = useApprovalFeatures('purchases')

  const [createIntegrationsJoinCompany, { loading: creatingCompany }] = useMutation(
    CreateJoinCompanyDocument,
    {
      extractorFn: (data) => data.integrationsJoinMutations.createIntegrationsjoinJoinCompany,
    }
  )
  const createJoinCompany = async () => {
    const result = await createIntegrationsJoinCompany({
      variables: {
        companyId: company.id,
      },
    })

    const handled = handleResult(result, (errors) => {
      return app.setFlash({
        type: 'wrong',
        message: errors.messages || i18n.t('common.save_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      refetch()
    }

    return handled
  }

  const handleJoinInstallation = (toggleValue: boolean) => {
    if (toggleValue === joinEnabled) return

    if (joinEnabled) {
      // TODO: handle delete company from join only
      return // removeJoinCompany() and updateAtsCompanySetting({ joinEnabled: false })
    }

    createJoinCompany()
  }

  const { data } = useQuery(AtsJoinPromotionSettingsDocument, {
    variables: {
      companyId: company.id,
    },
    fetchPolicy: 'cache-first',
  })

  const canManageAtsSettings = data?.ats._policies.manageAtsSettings.can ?? false
  const premiumPortalsEnabled =
    data?.features.enabledFeatures.nodes.find(
      (feature) => feature.name === 'dev_ats_join_premium_portals'
    )?.enabled ?? false
  const canSetJoinPurchaseRequestsApprovals = canManageAtsSettings && premiumPortalsEnabled

  return (
    <>
      <Section
        illustration="network"
        title={i18n.t('ats.settings.job_promotions.title')}
        description={i18n.t('ats.settings.job_promotions.description')}
        banner={
          <HelpCenterLink name="ats_integrations_join_how_to">
            {i18n.t('ats.settings.job_promotions.learn_more.title')}
          </HelpCenterLink>
        }
      >
        <Box gap="s12">
          <Card>
            {/* promote jobs  */}
            <Card.Title
              size="small"
              action={
                <TogglerCheckbox
                  value={joinEnabled}
                  disabled={creatingCompany || joinEnabled}
                  onChange={handleJoinInstallation}
                />
              }
            >
              {i18n.t('ats.settings.job_promotions.promote_jobs.title')}
            </Card.Title>
            <Card.Description>
              {i18n.t('ats.settings.job_promotions.promote_jobs.description')}
            </Card.Description>
            <Card.Footer>
              <Box gap="s8">
                <Link.Href href="https://join.com/privacy-policy" target="_blank" external>
                  JOIN privacy policy
                </Link.Href>
              </Box>
            </Card.Footer>
          </Card>
        </Box>
      </Section>
      {joinEnabled && (
        <Box gap="s12">
          {canSetJoinPurchaseRequestsApprovals && (
            <ApprovalsBlock
              category="premium_portals"
              title={i18n.t('ats.settings.job_promotions.purchase_requests.title')}
              description={i18n.t('ats.settings.job_promotions.purchase_requests.description')}
              multigroup
              icon={'document-approved'}
              hideAddGroupButton={!joinEnabled && !canAddGroup()}
              helpCenterUrlName="multiportal-integration"
            />
          )}
          <ExternalProfiles />
        </Box>
      )}
    </>
  )
}
